import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { message } from '@brokerbay/design-components';
import {
  getCardInfo,
  CardInfo,
  getStripeCustomerId,
  createStripeCustomerId,
  updateCardIntent,
} from 'api/BillingController';
import { getJpmErrorCodes } from 'api/SupraController';
import { featureFlagClient } from 'utils';
import { SupraController } from 'api';
import type { ProductType, CanManageType, OverdueBalanceType } from './index';

// Types

type ContextShape = {
  cardInfo?: CardInfo;
  setupIntentClientSecret?: string;
  loadingCardInfo: boolean;
  loadingCreateCustomerId: boolean;
  modalVisible: boolean;
  setModalVisible: Dispatch<SetStateAction<boolean>>;
  refetchCardInfo: () => Promise<void>;
  customerId?: string;
  userId?: string;
  productType: ProductType;
  canManageBilling: CanManageType;
  isSupraSubOverdue: OverdueBalanceType;
  createCustomer: () => Promise<void>;
  onCardUpdateSuccess?: () => void;
  isBillingPage?: boolean;
  showJPMC: boolean;
  jpmcSessionId: string;
  setLoadJPMCSession: Dispatch<SetStateAction<boolean>>;
  orgId?: string;
  billingCustomerNumber?: string;
  jpmErrorMap: Record<string, string>;
};

type ContextProps = {
  userId?: string;
  productType?: ProductType;
  orgId?: string;
  billingCustomerNumber?: string;
  canManageBilling: CanManageType;
  isSupraSubOverdue: OverdueBalanceType;
  onCardUpdateSuccess?: () => void;
  isBillingPage?: boolean;
  children?: React.ReactNode;
};

const defaultContext = {
  showJPMC: false,
} as ContextShape;

export const ManageCardContext =
  React.createContext<ContextShape>(defaultContext);

const isUsingSupraBilling = (
  orgId?: string,
  billingCustomerNumber?: string,
): boolean => (
    !!orgId &&
    !!billingCustomerNumber &&
    featureFlagClient.featureEnabled('supraBillingApiFeature') &&
    featureFlagClient.featureEnabled('keyholderBillingFeature')
  );

const fetchSupraBillingProfile = async (
  orgId: string,
  billingCustomerNumber: string,
) => {
  try {
    return await SupraController.getKeyholderBillingProfile({
      orgId,
      billingCustomerNumber,
    });
  } catch (error) {
    message.error('Failed to fetch billing profile');
    return undefined;
  }
};

const ManageCardContextProvider: React.FC<ContextProps> = ({
  children,
  userId,
  productType,
  orgId,
  billingCustomerNumber,
  canManageBilling,
  isSupraSubOverdue,
  onCardUpdateSuccess,
  isBillingPage,
}) => {
  const [showJPMC, setShowJPMC] = useState(false);
  const [loadJPMCSession, setLoadJPMCSession] = useState(true);
  const [customerId, setCustomerId] = useState<string>('');
  const [loadingCustomerId, setLoadingCustomerId] = useState(false);
  const [cardInfo, setCardInfo] = useState<CardInfo>();
  const [loadingCardInfo, setLoadingCardInfo] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [setupIntentClientSecret, setSetupIntentClientSecret] =
    useState<string>();
  const [loadingCreateCustomerId, setLoadingCreateCustomerId] = useState(false);
  const [jpmcSessionId, setJPMCSessionId] = useState<string>('');
  const [jpmErrorMap, setJpmErrorMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const evaluateFlags = async () => {
      if (isUsingSupraBilling(orgId, billingCustomerNumber)) {
        setShowJPMC(true);
      } else {
        setShowJPMC(false);
      }
    };
    evaluateFlags();
  }, []);

  const fetchCustomerId = useCallback(async () => {
    if (isUsingSupraBilling(orgId, billingCustomerNumber)) return;

    setLoadingCustomerId(true);
    try {
      const res = await getStripeCustomerId(productType, userId);
      setCustomerId(res);
    } catch (error) {
      message.error('Cannot fetch customerId');
    } finally {
      setLoadingCustomerId(false);
    }
  }, [productType, userId, orgId, billingCustomerNumber]);

  const fetchCardInfo = useCallback(async () => {
    // we need one of these
    if (!isUsingSupraBilling(orgId, billingCustomerNumber) && !customerId) {
      return;
    }
    setLoadingCardInfo(true);
    try {
      let res: CardInfo | undefined;

      if (isUsingSupraBilling(orgId, billingCustomerNumber)) {
        const profile = await fetchSupraBillingProfile(
          orgId!,
          billingCustomerNumber!,
        );
        if (profile) {
          res = {
            lastDigits: profile.MaskedCCNumber
              ? profile.MaskedCCNumber.slice(-4)
              : '****',
            type: profile.CCType ? profile.CCType.toLowerCase() : '',
            cardholderName: profile.CardholderName,
          };
        }
      } else {
        res = await getCardInfo(customerId);
      }

      if (res) setCardInfo(res);
    } catch (error) {
      message.error('Cannot fetch card info');
    } finally {
      setLoadingCardInfo(false);
    }
  }, [orgId, billingCustomerNumber, customerId]);

  const createCustomer = useCallback(async () => {
    if (!showJPMC) {
      try {
        const newCustomerId = await createStripeCustomerId(productType, userId);
        if (newCustomerId) {
          setCustomerId(newCustomerId);
        } else {
          message.error(
            'Unable to add card due to technical difficulty. Please try again later. If problem persists, please contact customer success team.',
          );
        }
      } catch (e) {
        message.error(
          'Unable to add card due to technical difficulty. Please ensure you are logged in as a user with permission to add payment methods. If problem persists, please contact customer success team.',
        );
      } finally {
        setLoadingCreateCustomerId(false);
      }
      setLoadingCreateCustomerId(true);
    }
    setModalVisible(true);
  }, [productType, showJPMC, userId]);

  const createSetUpIntent = useCallback(async () => {
    if (showJPMC) return;
    try {
      const { clientSecret } = await updateCardIntent(customerId);
      setSetupIntentClientSecret(clientSecret);
      if (!clientSecret) {
        message.error('Cannot update card due to technical difficulty.');
        setModalVisible(false);
      }
    } catch (e) {
      message.error('Cannot update card due to technical difficulty.');
      setModalVisible(false);
    }
  }, [customerId, showJPMC]);

  useEffect(() => {
    fetchCustomerId();
  }, [fetchCustomerId]);

  useEffect(() => {
    fetchCardInfo();
  }, [customerId, orgId, billingCustomerNumber, fetchCardInfo]);

  useEffect(() => {
    if (modalVisible && !showJPMC) {
      createSetUpIntent();
    } else {
      setSetupIntentClientSecret(undefined);
    }
  }, [modalVisible, createSetUpIntent, showJPMC]);

  useEffect(() => {
    if (!showJPMC || !cardInfo) return;

    const loadJpmcSession = async () => {
      try {
        const cssUrl = process.env.JPMC_IFRAME_CSS;
        const { cardholderName } = cardInfo;
        const sessionId = await SupraController.getJpmUidKeyholder({
          cssUrl,
          cardholderName,
        });
        setJPMCSessionId(sessionId);

        const errorMap = await getJpmErrorCodes();
        setJpmErrorMap(errorMap);
      } catch (e) {
        message.error('Unable to start Chase card session. Please try again.');
      }
      setLoadJPMCSession(false);
    };

    if (loadJPMCSession) loadJpmcSession();
  }, [
    loadJPMCSession,
    showJPMC,
    orgId,
    billingCustomerNumber,
    cardInfo,
    modalVisible,
  ]);

  return (
    <ManageCardContext.Provider
      value={{
        cardInfo,
        refetchCardInfo: fetchCardInfo,
        loadingCardInfo: loadingCardInfo || loadingCustomerId,
        setupIntentClientSecret,
        modalVisible,
        setModalVisible,
        customerId,
        userId,
        productType,
        canManageBilling,
        isSupraSubOverdue,
        createCustomer,
        loadingCreateCustomerId,
        onCardUpdateSuccess,
        isBillingPage,
        showJPMC,
        jpmcSessionId,
        setLoadJPMCSession,
        orgId,
        billingCustomerNumber,
        jpmErrorMap,
      }}
    >
      {children}
    </ManageCardContext.Provider>
  );
};

export default ManageCardContextProvider;
